import React, { useState } from "react";
import {
  FaHistory,
  FaChevronDown,
  FaChevronUp,
  FaFileImport,
  FaEdit,
  FaPlus,
  FaClock,
  FaUser,
} from "react-icons/fa";

const ContactEditHistory = ({ contact, onEdit }) => {
  const [showHistory, setShowHistory] = useState(false);
  const [showMetadata, setShowMetadata] = useState(false);

  const formatDate = (date) => {
    if (!date) return "N/A";

    // Handle Firestore Timestamp
    if (date.toDate) {
      return date.toDate().toLocaleString();
    }

    // Handle regular Date object
    if (date instanceof Date) {
      return date.toLocaleString();
    }

    // Handle date string
    return new Date(date).toLocaleString();
  };

  const getActionIcon = (action) => {
    switch (action) {
      case "imported":
        return <FaFileImport className="text-blue-500" />;
      case "created":
        return <FaPlus className="text-green-500" />;
      case "edited":
      case "updated_via_import":
        return <FaEdit className="text-orange-500" />;
      default:
        return <FaClock className="text-gray-500" />;
    }
  };

  const getSourceLabel = (source) => {
    switch (source) {
      case "hunter_csv_import":
        return "Hunter.io CSV Import";
      case "csv_import_ui":
        return "CSV Import (UI)";
      case "manual_entry":
        return "Manual Entry";
      case "manual_edit":
        return "Manual Edit";
      default:
        return source || "Unknown";
    }
  };

  return (
    <div className="bg-gray-50 rounded-lg p-4 mt-4">
      {/* Metadata Summary */}
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-sm font-medium text-gray-700 flex items-center">
          <FaClock className="mr-2" />
          Contact Information
        </h3>
        <button
          onClick={() => setShowMetadata(!showMetadata)}
          className="text-xs text-gray-500 hover:text-gray-700 flex items-center"
        >
          {showMetadata ? <FaChevronUp /> : <FaChevronDown />}
          <span className="ml-1">Details</span>
        </button>
      </div>

      {/* Quick metadata */}
      <div className="grid grid-cols-2 gap-4 text-xs text-gray-600 mb-3">
        <div>
          <span className="font-medium">Created:</span>{" "}
          {formatDate(contact.createdAt)}
        </div>
        <div>
          <span className="font-medium">Last Edited:</span>{" "}
          {formatDate(contact.lastEditedAt)}
        </div>
        <div>
          <span className="font-medium">Source:</span>{" "}
          {getSourceLabel(contact.source)}
        </div>
        <div>
          <span className="font-medium">Imported:</span>{" "}
          {formatDate(contact.importedAt)}
        </div>
      </div>

      {/* Expanded metadata */}
      {showMetadata && (
        <div className="border-t pt-3 mt-3">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-xs text-gray-600">
            <div>
              <span className="font-medium">Email:</span> {contact.email}
            </div>
            <div>
              <span className="font-medium">Company:</span>{" "}
              {contact.companyName || "N/A"}
            </div>
            <div>
              <span className="font-medium">Hunter Status:</span>{" "}
              {contact.hunterStatus || "N/A"}
            </div>
            <div>
              <span className="font-medium">Industry:</span>{" "}
              {contact.industry || "N/A"}
            </div>
            <div>
              <span className="font-medium">Location:</span>{" "}
              {contact.location || "N/A"}
            </div>
            <div>
              <span className="font-medium">LinkedIn:</span>
              {contact.linkedinUrl ? (
                <a
                  href={contact.linkedinUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-blue-500 hover:underline ml-1"
                >
                  View Profile
                </a>
              ) : (
                "N/A"
              )}
            </div>
          </div>

          {/* Rich Data Sections - Handle multiple field naming conventions */}
          {(contact.personSummary ||
            contact.person_summary ||
            contact.summary) && (
            <div className="mt-4">
              <h4 className="font-medium text-gray-700 mb-2">
                LinkedIn Profile Summary
              </h4>
              <div className="bg-gray-100 p-3 rounded text-xs max-h-32 overflow-y-auto">
                <pre className="whitespace-pre-wrap">
                  {contact.personSummary ||
                    contact.person_summary ||
                    contact.summary}
                </pre>
              </div>
            </div>
          )}

          {(contact.companySummary || contact.company_summary) && (
            <div className="mt-4">
              <h4 className="font-medium text-gray-700 mb-2">
                Company Summary
              </h4>
              <div className="bg-gray-100 p-3 rounded text-xs max-h-32 overflow-y-auto">
                <pre className="whitespace-pre-wrap">
                  {contact.companySummary || contact.company_summary}
                </pre>
              </div>
            </div>
          )}

          {(contact.workExperience ||
            contact.work_experience ||
            contact.work_ex) && (
            <div className="mt-4">
              <h4 className="font-medium text-gray-700 mb-2">
                Work Experience
              </h4>
              <div className="bg-gray-100 p-3 rounded text-xs max-h-32 overflow-y-auto">
                <pre className="whitespace-pre-wrap">
                  {contact.workExperience ||
                    contact.work_experience ||
                    contact.work_ex}
                </pre>
              </div>
            </div>
          )}

          {contact.education && (
            <div className="mt-4">
              <h4 className="font-medium text-gray-700 mb-2">Education</h4>
              <div className="bg-gray-100 p-3 rounded text-xs max-h-32 overflow-y-auto">
                <pre className="whitespace-pre-wrap">{contact.education}</pre>
              </div>
            </div>
          )}

          {(contact.linkedinSummary || contact.linkedin_summary) && (
            <div className="mt-4">
              <h4 className="font-medium text-gray-700 mb-2">
                LinkedIn Summary
              </h4>
              <div className="bg-gray-100 p-3 rounded text-xs max-h-32 overflow-y-auto">
                <pre className="whitespace-pre-wrap">
                  {contact.linkedinSummary || contact.linkedin_summary}
                </pre>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Edit History */}
      <div className="mt-4">
        <div className="flex items-center justify-between">
          <h4 className="text-sm font-medium text-gray-700 flex items-center">
            <FaHistory className="mr-2" />
            Edit History ({contact.editHistory?.length || 0})
          </h4>
          <button
            onClick={() => setShowHistory(!showHistory)}
            className="text-xs text-gray-500 hover:text-gray-700 flex items-center"
          >
            {showHistory ? <FaChevronUp /> : <FaChevronDown />}
            <span className="ml-1">{showHistory ? "Hide" : "Show"}</span>
          </button>
        </div>

        {showHistory && contact.editHistory && (
          <div className="mt-3 space-y-2 max-h-40 overflow-y-auto">
            {contact.editHistory.map((entry, index) => (
              <div
                key={index}
                className="flex items-start space-x-3 p-2 bg-white rounded border"
              >
                <div className="flex-shrink-0 mt-1">
                  {getActionIcon(entry.action)}
                </div>
                <div className="flex-grow min-w-0">
                  <div className="flex items-center justify-between">
                    <span className="text-xs font-medium text-gray-700 capitalize">
                      {entry.action.replace(/_/g, " ")}
                    </span>
                    <span className="text-xs text-gray-500">
                      {formatDate(entry.timestamp)}
                    </span>
                  </div>
                  <div className="text-xs text-gray-500 mt-1">
                    <div>Source: {getSourceLabel(entry.source)}</div>
                    {entry.by && (
                      <div className="flex items-center mt-1">
                        <FaUser className="mr-1" />
                        By: {entry.by}
                      </div>
                    )}
                    {entry.fields && entry.fields.length > 0 && (
                      <div className="mt-1">
                        Fields: {entry.fields.join(", ")}
                      </div>
                    )}
                    {entry.csvFile && (
                      <div className="mt-1">File: {entry.csvFile}</div>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Edit Actions */}
      <div className="mt-4 pt-3 border-t">
        <button
          onClick={() => onEdit && onEdit(contact)}
          className="text-sm bg-purple-600 text-white px-3 py-1 rounded hover:bg-purple-700 flex items-center"
        >
          <FaEdit className="mr-2" />
          Edit Contact
        </button>
      </div>
    </div>
  );
};

export default ContactEditHistory;
